import { Component, computed, ElementRef, inject, input, output, signal, ViewChild, linkedSignal } from '@angular/core';
import { TableModule } from '@openng/optimus-ui/table';
import { NgbDropdownModule, NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';

import { NgSelectModule } from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import {
  selectCategoryListByProductListId,
  selectProductList,
  selectSubcategories,
  setSelectedCategoryId,
  setSelectedSubcategoryId
} from '@zayan-erp/stores';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { effect } from '@angular/core';
import * as HomeBuildingActions from '@zayan-erp/stores';
import { rxResourceFixed } from '@zayan-erp/shared';
import { of, map } from 'rxjs';
import { Product, ProductService } from '@zayan-erp/home-building';
import { ToastModule } from '@openng/optimus-ui/toast';
import { ButtonModule } from '@openng/optimus-ui/button';
import { selectProductListId, selectCategoryId, selectSubCategoryId } from '@zayan-erp/stores';
import { NgbModalService, GenericDropdownComponent, InputFieldComponent, SvgIconsComponent } from '@zayan-erp/shared';

@Component({
  selector: 'lib-product-filter',
  imports: [
    GenericDropdownComponent,
    FormsModule,
    TableModule,
    NgbDropdownModule,
    NgSelectModule,
    AsyncPipe,
    NgbModalModule,
    ToastModule,
    ButtonModule,
    ReactiveFormsModule,
    InputFieldComponent,
    SvgIconsComponent
  ],
  templateUrl: './product-filter.component.html'
})
export class ProductFilterComponent {
  @ViewChild('producModelFilter') producModelFilter?: ElementRef;

  #ngbModelService = inject(NgbModalService);
  #modalService = inject(NgbModal);
  #store = inject(Store);
  #productService = inject(ProductService);
  productListId = input<string | null>(null);
  isProductListDisabled = input<boolean>(false);
  disabled = input<boolean>(false);
  initialProductTitle = input<string>('');
  label = input<string>('Product List');
  placeholder = input<string>('');
  selectedProductListId = signal<string | null>(this.#store.selectSignal(selectProductListId)());
  selectedCategoryId = signal<string | null>(this.#store.selectSignal(selectCategoryId)());
  selectedSubcategoryId = signal<string | null>(this.#store.selectSignal(selectSubCategoryId)());
  selectedProductTitle = linkedSignal(() => this.initialProductTitle() || '');
  search = signal<string>('');
  showLabel = input<boolean>(false);
  required = input<boolean>(false);
  selectedProduct = output<Product>();
  closeModal = output<void>();

  displayPlaceholder = computed(() => this.placeholder() || `Select ${this.label()}`);

  productList$ = this.#store.select(selectProductList);
  categoryListByProductListId$ = this.#store.select(selectCategoryListByProductListId(this.selectedProductListId()!));
  subcategories$ = this.#store.select(selectSubcategories(this.selectedCategoryId()!));

  constructor() {
    effect(() => {
      const productListIdInput = this.productListId();
      if (productListIdInput) {
        this.selectedProductListId.set(productListIdInput);
      }

      const selectedProdListId = this.selectedProductListId();
      if (selectedProdListId) {
        this.#store.dispatch(
          HomeBuildingActions.loadCategoryListByProductListId({ productListId: selectedProdListId })
        );
      }
      this.categoryListByProductListId$ = this.#store.select(selectCategoryListByProductListId(selectedProdListId!));
    });

    effect(() => {
      if (this.selectedCategoryId()) {
        this.#store.dispatch(
          HomeBuildingActions.loadSubcategories({
            productListId: this.selectedProductListId()!,
            categoryListId: this.selectedCategoryId()!
          })
        );
      }
      this.subcategories$ = this.#store.select(selectSubcategories(this.selectedCategoryId()!));
    });
  }

  productRxResource = rxResourceFixed({
    params: () => ({
      productListId: this.selectedProductListId()!,
      categoryId: this.selectedCategoryId()!,
      subcategoryId: this.selectedSubcategoryId()!
    }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.productListId) {
        return this.#productService
          .readProducts(params.productListId, params.categoryId, params.subcategoryId, 1, 1000)
          .pipe(map((response: { items: Product[]; totalCount: number }) => response?.items ?? []));
      }
      return of([]);
    }
  });

  productDisplay = computed(() => {
    return this.productRxResource
      .value()
      ?.filter(
        (product: any) =>
          product.code.toLowerCase().includes(this.search().toLowerCase()) ||
          product.description?.toLowerCase().includes(this.search().toLowerCase())
      );
  });

  openProductModelFilter() {
    if (this.disabled()) return;
    this.#modalService.open(this.producModelFilter, { ...this.#ngbModelService.centerModal, size: 'xxl' });
  }

  selectProduct(product: Product) {
    this.selectedProduct.emit(product);
    this.selectedProductTitle.set(product.code);
    this.closeProductModelFilter();
  }

  onProductListChange(productListId: string | null) {
    this.selectedProductListId.set(productListId);
    this.#store.dispatch(setSelectedCategoryId({ categoryId: null }));
    this.#store.dispatch(setSelectedSubcategoryId({ subcategoryId: null }));
    this.selectedCategoryId.set(null);
    this.selectedSubcategoryId.set(null);
  }

  onCategoryChange(categoryId: string | null) {
    this.#store.dispatch(setSelectedCategoryId({ categoryId }));
    this.#store.dispatch(setSelectedSubcategoryId({ subcategoryId: null }));
    this.selectedSubcategoryId.set(null);
  }

  onSubcategoryChange(subcategoryId: string | null) {
    this.#store.dispatch(setSelectedSubcategoryId({ subcategoryId }));
  }

  closeProductModelFilter() {
    this.#modalService.dismissAll();
    this.closeModal.emit();
  }
}
